import React, { useState } from 'react';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import FormField from '@ingka/form-field';
import styled from 'styled-components';
import InputField from '@ingka/input-field';
import { removeEmojis } from '../../utils/emojiFilter';

export interface HashFullWidthProps {
  fullwidth?: string;
}

type Value = string | number;

interface ValidatedInputFieldProps extends HashFullWidthProps {
  id: string;
  type: string;
  label: string;
  value: Value;
  onChange: (val: React.ChangeEvent<HTMLInputElement>) => void;
  validatedField: string | null;
  onBlur?: () => void;
  req: boolean;
  isSubmitBtnClicked: boolean;
  disabled: boolean;
  autocomplete: string;
}

const StyledFormField = styled(FormField)`
  width: ${(props: HashFullWidthProps): string =>
    props.fullwidth ? '100%' : 'calc(50% - 10px)'};
`;

const StyledInputField = styled(InputField)`
  width: 100%;
  display: inline-block;
  margin: 0 0 0px;

  & input {
    width: 100%;
  }
`;

export const ValidatedInputField: React.FC<ValidatedInputFieldProps> = ({
  id,
  type,
  label,
  value,
  onChange,
  onBlur = (): void => {
    /**/
  },
  validatedField,
  fullwidth,
  req,
  isSubmitBtnClicked,
  disabled,
  autocomplete,
}): JSX.Element => {
  const { t } = useTranslation();
  const [validationId] = useState(`invalid-${Math.random().toString(36)}`);
  const isValid = !(isSubmitBtnClicked && validatedField !== null);

  // Handler to filter out emojis from input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value: inputValue } = e.target;
    const filteredValue = removeEmojis(inputValue);

    // Only call onChange if value was modified or is valid
    if (filteredValue !== inputValue) {
      // Create a new event with the filtered value
      const modifiedEvent = {
        ...e,
        target: {
          ...e.target,
          value: filteredValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(modifiedEvent);
    } else {
      onChange(e);
    }
  };

  return (
    <StyledFormField
      valid={isValid}
      shouldValidate={isSubmitBtnClicked}
      validation={{
        id: validationId,
        msg: t(validatedField as string),
      }}
      {...(fullwidth ? { fullwidth: fullwidth.toString() } : {})}
    >
      <StyledInputField
        id={id}
        type={type}
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        describedById={isValid ? undefined : validationId}
        req={req}
        disabled={disabled}
        autoComplete={autocomplete}
      />
    </StyledFormField>
  );
};
